import { useMemo } from 'react';
import type { JSX } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import {
  HELP_FORBIDDEN_COPY,
  HELP_MISSING_COPY,
  isHelpSlug,
  sessionCanReadHelp,
  visibleHelpBrowse,
  visibleHelpChooser,
  type HelpDocument,
} from '@inventohub/shared';
import { HelpChooserIcon } from '../help/HelpChooserIcon';
import { HelpMarkdown } from '../help/HelpMarkdown';
import { HELP_DOCUMENTS, helpDocumentBySlug } from '../help/load';
import { useAuth } from '../lib/auth';

const SECTION_ORDER = [
  'What this is',
  'When you use it',
  'Steps',
  'What the buttons and statuses mean',
  'Related',
] as const;

export function HelpPage(): JSX.Element {
  const { slug } = useParams();
  const location = useLocation();
  const { user, code, loading } = useAuth();
  const requested = slug?.trim() ?? '';
  const signedIn = Boolean(user || code);
  const allowSignedOut = requested === 'sign-in';
  const chooser = useMemo(() => visibleHelpChooser(HELP_DOCUMENTS, user, code), [user, code]);
  const browse = useMemo(() => visibleHelpBrowse(HELP_DOCUMENTS, user, code), [user, code]);

  if (loading) {
    return (
      <p className="meta" data-testid="auth-loading">
        Checking sign-in…
      </p>
    );
  }

  if (!signedIn && !allowSignedOut) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (!requested) {
    return (
      <section data-testid="help-page">
        <HelpHome chooser={chooser} browse={browse} />
      </section>
    );
  }

  const doc = helpDocumentBySlug(requested);
  if (!doc || !isHelpSlug(requested)) {
    return (
      <section data-testid="help-page">
        <p className="status status--warn" data-testid="help-missing">
          {HELP_MISSING_COPY}
        </p>
        <HelpHome chooser={chooser} browse={browse} />
      </section>
    );
  }

  if (!sessionCanReadHelp(doc, user, code)) {
    return (
      <section data-testid="help-page">
        <p className="status status--warn" data-testid="help-forbidden">
          {HELP_FORBIDDEN_COPY}
        </p>
        <HelpHome chooser={chooser} browse={browse} />
      </section>
    );
  }

  return (
    <section data-testid="help-page">
      <HelpArticle article={doc} />
    </section>
  );
}

function HelpHome({
  chooser,
  browse,
}: {
  chooser: ReturnType<typeof visibleHelpChooser>;
  browse: ReturnType<typeof visibleHelpBrowse>;
}): JSX.Element {
  return (
    <>
      <div className="card">
        <h2>Help</h2>
        <p className="meta">Pick a job, or browse the playbooks you can see with this sign-in.</p>
      </div>

      {chooser.length > 0 ? (
        <div className="card" data-testid="help-chooser">
          <h2>What do you want to do?</h2>
          <div className="service-grid">
            {chooser.map((article) => (
              <Link
                key={article.slug}
                to={`/help/${article.slug}`}
                className="service-tile"
                data-testid={`help-chooser-${article.slug}`}
              >
                <HelpChooserIcon slug={article.slug} />
                <span className="service-tile__label">{article.chooserLabel}</span>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      {browse.length > 0 ? (
        <div className="card" data-testid="help-browse">
          <h2>Browse</h2>
          {browse.map((group) => (
            <section key={group.id} data-testid={`help-browse-${group.id}`}>
              <h3 className="help-browse__title">{group.label}</h3>
              <ul className="link-list">
                {group.articles.map((article) => (
                  <li key={article.slug}>
                    <Link to={`/help/${article.slug}`} data-testid={`help-browse-${article.slug}`}>
                      {article.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : null}
    </>
  );
}

function HelpArticle({ article }: { article: HelpDocument }): JSX.Element {
  const { user, code } = useAuth();
  const sections = SECTION_ORDER.map((heading) => {
    const section = article.sections.find((entry) => entry.heading === heading);
    if (!section || !section.markdown.trim()) return null;
    return (
      <section key={heading} data-testid={`help-section-${slugify(heading)}`}>
        <h3>{heading}</h3>
        <HelpMarkdown source={section.markdown} user={user} code={code} />
      </section>
    );
  }).filter(Boolean);

  return (
    <article className="help-article" data-testid="help-article">
      <p className="meta">
        <Link to="/help" data-testid="help-home-link">
          Help
        </Link>
      </p>
      <h2 data-testid="help-article-title">{article.title}</h2>
      {sections}
    </article>
  );
}

function slugify(heading: string): string {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
